import { Grid, Typography } from '@mui/material';
import FieldText from '../../../../../components/general/fields/fieldText';
import { useQuotationCallInfo } from './hook';
import { QuotationCallInfoProps } from './props';
import AutocompleteField from '../../../../../components/general/fields/autocompleteField';
import { formatParams } from '../../../../../../shared/utils/params';
import RichText from '../../../../../components/richText';
import DatePickerField from '../../../../../components/general/fields/datePickerField';
import dayjs from 'dayjs';

export const QuotationCallInfo = (props: QuotationCallInfoProps) => {
    const { isRestrictedRole } = props;
    const {
        formik,
        bouquets,
        channels,
        typeQuotations,
        typifications,
        subtypifications,
        status,
        stages,
        motives,
        isDispatch,
        isPending,
    } = useQuotationCallInfo(props);

    return (
        <Grid
            container
            spacing={{ xs: 1.5, md: 1.5 }}
            columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
            pt="20px"
        >
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 'bold', color: '#000' }}>
                    Información de la llamada
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FieldText
                    label="Call ID"
                    name="callId"
                    size="small"
                    onBlur={formik.handleBlur}
                    touched={formik.touched.callId}
                    errors={formik.errors.callId}
                    value={formik.values.callId}
                    onChange={formik.handleChange}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <AutocompleteField
                    data={formatParams(channels)}
                    label="Canal"
                    name="channel"
                    size="small"
                    required
                    touched={formik.touched.channel}
                    errors={formik.errors.channel}
                    value={formik.values.channel as string}
                    onChangeValue={(value: any) =>
                        formik.setFieldValue('channel', value)
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 'bold', color: '#000' }}>
                    Información de la cotización
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <AutocompleteField
                    data={formatParams(typeQuotations)}
                    label="Tipo de cotización"
                    name="typeQuotation"
                    size="small"
                    required={!isRestrictedRole}
                    touched={formik.touched.typeQuotation}
                    errors={formik.errors.typeQuotation}
                    value={formik.values.typeQuotation as string}
                    onChangeValue={(value: any) =>
                        formik.setFieldValue('typeQuotation', value)
                    }
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <AutocompleteField
                    data={formatParams(bouquets)}
                    label="Ramo"
                    name="bouquet"
                    size="small"
                    required
                    touched={formik.touched.bouquet}
                    errors={formik.errors.bouquet}
                    value={formik.values.bouquet as string}
                    onChangeValue={(value: any) =>
                        formik.setFieldValue('bouquet', value)
                    }
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <AutocompleteField
                    data={formatParams(status)}
                    label="Estado"
                    name="status"
                    size="small"
                    required={!isRestrictedRole}
                    touched={formik.touched.status}
                    errors={formik.errors.status}
                    value={formik.values.status as string}
                    onChangeValue={(value: any) =>
                        formik.setFieldValue('status', value)
                    }
                    disabled={isRestrictedRole}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <AutocompleteField
                    data={formatParams(typifications)}
                    label="Tipificación"
                    name="typification"
                    size="small"
                    required
                    touched={formik.touched.typification}
                    errors={formik.errors.typification}
                    value={formik.values.typification as string}
                    onChangeValue={(value: any) =>
                        formik.setFieldValue('typification', value)
                    }
                />
            </Grid>
            {subtypifications.length > 0 && (
                <Grid item xs={12} sm={6} md={4}>
                    <AutocompleteField
                        data={formatParams(subtypifications)}
                        label="Subtipificación"
                        name="subtypification"
                        size="small"
                        required
                        touched={formik.touched.subtypification}
                        errors={formik.errors.subtypification}
                        value={formik.values.subtypification as string}
                        onChangeValue={(value: any) =>
                            formik.setFieldValue('subtypification', value)
                        }
                    />
                </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
                <AutocompleteField
                    data={formatParams(stages)}
                    label="Etapa"
                    name="stage"
                    size="small"
                    required={!isRestrictedRole}
                    touched={formik.touched.stage}
                    errors={formik.errors.stage}
                    value={formik.values.stage as string}
                    onChangeValue={(value: any) =>
                        formik.setFieldValue('stage', value)
                    }
                    disabled={isRestrictedRole}
                />
            </Grid>
            {isDispatch && (
                <>
                    <Grid item xs={12} sm={6} md={4}>
                        <FieldText
                            label="Número de póliza"
                            name="policyNumber"
                            size="small"
                            required
                            touched={formik.touched.policyNumber}
                            errors={formik.errors.policyNumber}
                            value={formik.values.policyNumber}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <DatePickerField
                            label="Fecha de expedición de póliza"
                            name="policyDate"
                            size="small"
                            required
                            touched={formik.touched.policyDate}
                            errors={formik.errors.policyDate?.toString()}
                            value={
                                formik.values.policyDate
                                    ? dayjs(formik.values.policyDate)
                                    : null
                            }
                            onChange={(value) =>
                                formik.setFieldValue(
                                    'policyDate',
                                    value ? value.toString() : undefined,
                                    true
                                )
                            }
                        />
                    </Grid>
                </>
            )}
            {isPending && (
                <Grid item xs={12} sm={6} md={4}>
                    <AutocompleteField
                        data={formatParams(motives)}
                        label="Motivo"
                        name="motive"
                        size="small"
                        required
                        touched={formik.touched.motive}
                        errors={formik.errors.motive}
                        value={formik.values.motive as string}
                        onChangeValue={(value: any) =>
                            formik.setFieldValue('motive', value)
                        }
                    />
                </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
                <FieldText
                    label="Placa"
                    name="plate"
                    size="small"
                    onBlur={formik.handleBlur}
                    touched={formik.touched.plate}
                    errors={formik.errors.plate}
                    value={formik.values.plate}
                    onChange={(e) => {
                        if (!isRestrictedRole) {
                            formik.handleChange(e);
                        }
                    }}
                    inputProps={{ maxLength: 10 }}
                    disabled={isRestrictedRole}
                    required={!isRestrictedRole}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FieldText
                    label="Importe"
                    name="amount"
                    size="small"
                    onBlur={formik.handleBlur}
                    touched={formik.touched.amount}
                    errors={formik.errors.amount}
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    required={!isRestrictedRole}
                />
            </Grid>

            <Grid item xs={12}>
                <FieldText
                    label="Observación"
                    name="observation"
                    size="small"
                    required
                    rows={3}
                    onBlur={formik.handleBlur}
                    touched={formik.touched.observation}
                    errors={formik.errors.observation}
                    value={formik.values.observation}
                    onChange={formik.handleChange}
                />
            </Grid>
            <Grid item xs={12}>
                <RichText
                    initialContent={formik.values.body}
                    editable
                    onContentChange={(value) =>
                        formik.setFieldValue('body', value)
                    }
                />
            </Grid>
        </Grid>
    );
};
